import { FC } from 'react';
import { Stack, Paper, Button } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Questionnaire, QuestionnaireResponse } from '@medplum/fhirtypes';
import BirthPeriodListContent from './BirthPeriodListContent';
import { notifications } from '@mantine/notifications';
import { ListContentAction } from '../utils/medical-history.types';

export interface BirthPeriodListProps {
  periodType: 'perinatalPeriod' | 'prenatalPeriod';
  questionnaire: Questionnaire | undefined;
  questionnaireResponse: QuestionnaireResponse | null | undefined;
  onAdd: () => void;
  onDelete: () => Promise<void>;
  readonly?: boolean;
}

export const BirthPeriodList: FC<BirthPeriodListProps> = ({
  periodType,
  questionnaire,
  questionnaireResponse,
  onAdd,
  onDelete,
  readonly = false,
}) => {
  const { t } = useTranslation(['patient-pages']);

  const hasData =
    questionnaireResponse &&
    (questionnaireResponse.status === 'completed' ||
      questionnaireResponse.status === 'in-progress');

  const handleAction = async (action: ListContentAction) => {
    if (action === 'edit') {
      onAdd();
      return;
    }

    if (action === 'remove') {
      try {
        await onDelete();
        notifications.show({
          color: 'green',
          title: t('medicalHistory.deleteSuccess', {
            ns: 'patient-pages',
            defaultValue: 'Deleted',
          }),
          message: t(`medicalHistory.${periodType}.deleteSuccessMessage`, {
            ns: 'patient-pages',
            defaultValue: `${periodType} deleted successfully`,
          }),
        });
      } catch (error) {
        console.error(`Error deleting ${periodType}:`, error);
        notifications.show({
          color: 'red',
          title: t('medicalHistory.deleteError', {
            ns: 'patient-pages',
            defaultValue: 'Error',
          }),
          message: t(`medicalHistory.${periodType}.deleteErrorMessage`, {
            ns: 'patient-pages',
            defaultValue: `Failed to delete ${periodType}`,
          }),
        });
      }
    }
  };

  return (
    <Stack gap="xs">
      <Paper
        withBorder
        radius="md"
        p="sm"
        bg={hasData ? 'transparent' : 'gray.0'}
        pb={hasData ? 'sm' : 'xl'}
      >
        <BirthPeriodListContent
          periodType={periodType}
          questionnaire={questionnaire}
          questionnaireResponse={questionnaireResponse}
          hasData={hasData}
          onAction={handleAction}
          readonly={readonly}
        />
      </Paper>

      {!readonly && !hasData && (
        <Button
          leftSection={<IconPlus size={16} />}
          variant="light"
          size="sm"
          onClick={onAdd}
        >
          {t(`medicalHistory.${periodType}.add`, {
            defaultValue: `Add ${periodType} Data`,
          })}
        </Button>
      )}
    </Stack>
  );
};

export default BirthPeriodList;
